'use client'

import { XIcon } from '@phosphor-icons/react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react'
import { cn } from '@/core/lib/utils'
import Button from '@/src/components/core/Button'
import Search from '@/src/components/flch/components/Search'
import useEventListener from '@/src/hooks/useEventListener'

const SearchDialogContext = createContext<() => void>(() => {})

export const useSearchDialog = () => useContext(SearchDialogContext)

export const SearchDialogProvider = ({ children }: { children: ReactNode }) => {
  const [open, setOpen] = useState(false)

  const openSearch = useCallback(() => setOpen(true), [])

  useEventListener(
    'keydown',
    useCallback((event: Event) => {
      const { key, metaKey, ctrlKey } = event as KeyboardEvent
      if (key.toLowerCase() !== 'k' || !(metaKey || ctrlKey)) return
      event.preventDefault()
      setOpen((current) => !current)
    }, [])
  )

  return (
    <SearchDialogContext.Provider value={openSearch}>
      {children}
      <SearchDialog open={open} onOpenChange={setOpen} />
    </SearchDialogContext.Provider>
  )
}

const SearchDialog = ({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) => {
  const contentRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            'fixed inset-0 z-50 backdrop-blur-md',
            'bg-linear-to-b from-gray-50 via-gray-50/95 to-gray-50/70',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0% data-[state=open]:animate-in data-[state=open]:fade-in-0%'
          )}
        />
        <DialogPrimitive.Content
          ref={contentRef}
          aria-describedby={undefined}
          onOpenAutoFocus={(event) => {
            event.preventDefault()
            inputRef.current?.focus()
          }}
          className={cn(
            'fixed inset-0 z-50 overflow-y-auto',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0% data-[state=open]:animate-in data-[state=open]:fade-in-0%'
          )}
        >
          <DialogPrimitive.Close asChild>
            <Button
              size="icon-small"
              version="gray-outlined"
              icon={<XIcon weight="bold" />}
              aria-label="Schliessen"
              className="absolute top-4 right-4 lg:top-8 lg:right-8"
            />
          </DialogPrimitive.Close>
          <div className="flex min-h-full flex-col items-center px-5 pt-20 pb-10 lg:pt-26">
            <DialogPrimitive.Title className="max-w-200 text-center text-2xl font-black text-balance lg:text-7xl">
              Entdecke deine Fahrausbildung in der Nähe
            </DialogPrimitive.Title>
            <div className="mt-6 w-full max-w-200 lg:mt-10">
              <Search
                portalContainer={contentRef}
                onNavigate={() => onOpenChange(false)}
                inputRef={inputRef}
              />
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
